/**
 * Push Notification Communications Hook
 * Manages push notification queue and template data fetching and mutations
 */

import { useState, useCallback } from 'react';
import { usePushNotificationService, useTemplateService } from '@actschurch/ccms-sdk';
import type { AppPushMessage, AppPushMessageModelView } from '@actschurch/ccms-sdk';
import type { AppPushTemplateModelView } from '@actschurch/ccms-sdk';
import { toast } from '../../../../../components/ui/providers/AlertProvider';

export function usePushCommunications() {
  const pushService = usePushNotificationService();
  const templateService = useTemplateService();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queueItems, setQueueItems] = useState<AppPushMessageModelView[]>([]);
  const [templates, setTemplates] = useState<AppPushTemplateModelView[]>([]);

  // ==================== QUEUE OPERATIONS ====================

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pushService.getMessages();
      setQueueItems(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch push notification queue';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [pushService]);

  const fetchQueueItem = useCallback(
    async (id: number): Promise<AppPushMessageModelView> => {
      try {
        return await pushService.getMessageById(id);
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch push notification ${id}`;
        setError(errorMessage);
        throw err;
      }
    },
    [pushService]
  );

  const deleteQueueItem = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await toast.promise(pushService.deleteMessage(id), {
          loading: 'Deleting push notification...',
          success: 'Push notification deleted successfully',
          error: (err) => `Failed to delete: ${err.message}`,
        });
        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to delete push notification');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [pushService, fetchQueue]
  );

  /**
   * Deletes the whole selection in ONE request. Deleting 100 notifications one call at a time
   * was the reason bulk delete was unusable.
   */
  const bulkDeleteQueueItems = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;

      setLoading(true);
      try {
        const result = await toast.promise(pushService.deleteMessages(ids), {
          loading: `Deleting ${ids.length} push notification(s)...`,
          success: (res) =>
            res.notFoundIds.length > 0
              ? `Deleted ${res.deleted} of ${ids.length} (${res.notFoundIds.length} already gone)`
              : `${res.deleted} push notification(s) deleted successfully`,
          error: (err) => `Failed to delete: ${err.message}`,
        });
        await fetchQueue();
        return result;
      } catch (err: any) {
        setError(err?.message || 'Failed to delete push notifications');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [pushService, fetchQueue]
  );

  const retryFailedItem = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        // Dedicated endpoint: it clears the failure state (including the exhausted retry budget) and
        // leaves the token/payload intact. PUT-ing the view model back would blank the device token.
        await toast.promise(pushService.retryMessage(id), {
          loading: 'Requeueing push notification...',
          success: 'Push notification requeued — the worker will retry it shortly',
          error: (err) => `Failed to retry: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to retry push notification');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [pushService, fetchQueue]
  );

  const updatePriority = useCallback(
    async (id: number, priority: number) => {
      setLoading(true);
      try {
        const item = queueItems.find((q) => q.messageID === id);
        if (!item) throw new Error('Push notification not found');

        await toast.promise(
          pushService.updateMessage(id, { ...item, priority } as any),
          {
            loading: 'Updating priority...',
            success: 'Priority updated successfully',
            error: (err) => `Failed to update priority: ${err.message}`,
          }
        );

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to update priority');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [pushService, fetchQueue, queueItems]
  );

  const updateQueueItem = useCallback(
    async (id: number, data: Partial<AppPushMessage>) => {
      setLoading(true);
      try {
        await toast.promise(pushService.updateMessage(id, data as AppPushMessage), {
          loading: 'Updating push notification...',
          success: 'Push notification updated successfully',
          error: (err) => `Failed to update: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to update push notification');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [pushService, fetchQueue]
  );

  const createPush = useCallback(
    async (data: Partial<AppPushMessage>) => {
      setLoading(true);
      try {
        await toast.promise(pushService.createMessage(data as AppPushMessage), {
          loading: 'Queuing push notification...',
          success: 'Push notification queued successfully',
          error: (err) => `Failed to queue: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to create push notification');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [pushService, fetchQueue]
  );

  // ==================== TEMPLATE OPERATIONS ====================

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await templateService.getPushTemplates();
      setTemplates(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch push templates';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [templateService]);

  const fetchTemplate = useCallback(
    async (id: number): Promise<AppPushTemplateModelView> => {
      try {
        return await templateService.getPushTemplate(id);
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch template ${id}`;
        setError(errorMessage);
        throw err;
      }
    },
    [templateService]
  );

  const createTemplate = useCallback(
    async (data: any) => {
      setLoading(true);
      try {
        await toast.promise(templateService.createPushTemplate(data), {
          loading: 'Creating template...',
          success: 'Template created successfully',
          error: (err) => `Failed to create template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to create template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  const updateTemplate = useCallback(
    async (id: number, data: any) => {
      setLoading(true);
      try {
        await toast.promise(templateService.updatePushTemplate(id, data), {
          loading: 'Updating template...',
          success: 'Template updated successfully',
          error: (err) => `Failed to update template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to update template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  const deleteTemplate = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await toast.promise(templateService.deletePushTemplate(id), {
          loading: 'Deleting template...',
          success: 'Template deleted successfully',
          error: (err) => `Failed to delete template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to delete template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  // ==================== COMBINED FETCH ====================

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchQueue(), fetchTemplates()]);
  }, [fetchQueue, fetchTemplates]);

  return {
    loading,
    error,
    setError,

    // Queue
    queueItems,
    fetchQueue,
    fetchQueueItem,
    deleteQueueItem,
    bulkDeleteQueueItems,
    retryFailedItem,
    updatePriority,
    updateQueueItem,
    createPush,

    // Templates
    templates,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,

    // Combined
    fetchAll,
  };
}
